import React from 'react';
import { useNavigate } from 'react-router-dom';

const services = [
  {
    id: 'tag-conversion',
    title: 'Tag Conversion Agent',
    description: 'Upload Excel/CSV files for async tag and UDT conversion processing using advanced AI algorithms',
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
        <line x1="10" y1="9" x2="8" y2="9"></line>
      </svg>
    ),
    path: '/upload',
    color: '#7470a6',
    gradient: 'linear-gradient(135deg, rgba(116, 112, 166, 0.12) 0%, rgba(251, 205, 46, 0.08) 100%)'
  },
  {
    id: 'logic-conversion',
    title: 'Quick Script Conversion Agent',
    description: 'Convert Wonderware Quick Script to Ignition Jython for seamless system migration',
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6"></polyline>
        <polyline points="8 6 2 12 8 18"></polyline>
      </svg>
    ),
    path: '/logic',
    color: '#7470a6',
    gradient: 'linear-gradient(135deg, rgba(116, 112, 166, 0.12) 0%, rgba(251, 205, 46, 0.08) 100%)'
  },
  {
    id: 'sql-optimization',
    title: 'SQL Optimization Agent',
    description: 'Optimize and convert SQL queries for Ignition database operations with intelligent analysis',
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <ellipse cx="12" cy="5" rx="9" ry="3"></ellipse>
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path>
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path>
      </svg>
    ),
    path: '/sql',
    color: '#7470a6',
    gradient: 'linear-gradient(135deg, rgba(116, 112, 166, 0.12) 0%, rgba(251, 205, 46, 0.08) 100%)'
  },
  {
    id: 'validation',
    title: 'Tag Validation Agent',
    description: 'Validate converted tags by comparing source CSV with JSON output for quality assurance',
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
        <polyline points="22 4 12 14.01 9 11.01"></polyline>
      </svg>
    ),
    path: '/validation',
    color: '#7470a6',
    gradient: 'linear-gradient(135deg, rgba(116, 112, 166, 0.12) 0%, rgba(251, 205, 46, 0.08) 100%)'
  },
  {
    id: 'videos',
    title: 'Video Library',
    description: 'Watch migration demos and training clips directly in the application with playback controls',
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="23 7 16 12 23 17 23 7"></polygon>
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
      </svg>
    ),
    path: '/videos',
    color: '#7470a6',
    gradient: 'linear-gradient(135deg, rgba(116, 112, 166, 0.12) 0%, rgba(251, 205, 46, 0.08) 100%)'
  }
];

const hasScadaMigrationModuleAccess = (auth) => {
  const modules = auth?.user?.moduleAccess || auth?.moduleAccess || [];
  if (!Array.isArray(modules)) {
    return false;
  }

  return modules.some((entry) => {
    if (typeof entry === 'string') {
      return entry.trim().toUpperCase() === 'SCADA_MIGRATION';
    }

    const moduleName = entry?.module || entry?.name || entry?.moduleName;
    const isEntryActive = entry?.isActive;
    return String(moduleName || '').trim().toUpperCase() === 'SCADA_MIGRATION'
      && (isEntryActive === undefined || isEntryActive === true);
  });
};

function LandingPage({ auth }) {
  const navigate = useNavigate();
  const canUseAgentCards = hasScadaMigrationModuleAccess(auth);

  return (
    <div>
      <div className="hero-section" style={{ paddingTop: '2rem', paddingBottom: '3rem' }}>
        <h1 className="hero-title">L48 Migration Agents "Legacy SCADA to Ignition SCADA"</h1>
        <p className="hero-subtitle">
          Transforming legacy systems to next-generation technology with AI-powered conversion and optimization
        </p>
      </div>

      <div className="services-grid">
        {services.map((service) => (
          <div
            key={service.id}
            className={`card ${canUseAgentCards ? '' : 'card-disabled'}`}
            onClick={() => {
              if (canUseAgentCards) {
                navigate(service.path);
              }
            }}
            role="button"
            aria-disabled={!canUseAgentCards}
            tabIndex={canUseAgentCards ? 0 : -1}
            onKeyPress={(e) => {
              if (canUseAgentCards && e.key === 'Enter') {
                navigate(service.path);
              }
            }}
          >
            <div 
              className="card-icon" 
              style={{ background: service.gradient, color: service.color }}
            >
              {service.icon}
            </div>
            <h3 className="card-title" style={{ color: service.color }}>
              {service.title}
            </h3>
            <p className="card-description">
              {service.description}
            </p>
            <span className="card-link" style={{ color: service.color }}>
              {canUseAgentCards ? 'Explore' : 'Access Restricted'}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </span>
          </div>
        ))}
      </div>

      <div className="services-section" style={{ marginTop: '4rem', paddingTop: '2rem', borderTop: '1px solid var(--border)' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1.5rem', color: 'var(--text-primary)' }}>
          Powered by Wipro Intelligence
        </h2>
        <div className="features-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.5rem' }}>
          <div style={{ padding: '1.5rem', background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>🚀</div>
            <h4 style={{ fontWeight: '600', marginBottom: '0.5rem', color: 'var(--text-primary)' }}>Rapid Migration</h4>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Accelerate system transitions with intelligent automation</p>
          </div>
          <div style={{ padding: '1.5rem', background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>✓</div>
            <h4 style={{ fontWeight: '600', marginBottom: '0.5rem', color: 'var(--text-primary)' }}>Quality Assurance</h4>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Comprehensive validation to ensure code integrity</p>
          </div>
          <div style={{ padding: '1.5rem', background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>⚡</div>
            <h4 style={{ fontWeight: '600', marginBottom: '0.5rem', color: 'var(--text-primary)' }}>Performance</h4>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Optimized solutions for maximum efficiency</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LandingPage;
